import { getPrice, updatePrice } from '@/adapters/master';
import { IPrice, IPriceRes, IApiResponse } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  notification,
  Table,
  TableColumnsType,
  Badge,
  Input,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { uniqueId } from 'lodash';
import dayjs from 'dayjs';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { errors?: { errorMessage?: string } };
}

const PricePage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IPrice[]>([]);
  const [selected, setSelected] = useState<IPrice[]>([]);
  const [searchText, setSearchText] = useState('');

  const { data, refetch, isFetching } = useQuery<IPriceRes, Error>({
    queryKey: ['get-price'],
    queryFn: getPrice,
    retry: 0,
  });

  const notifyBatchErrors = (title: string, res: IApiResponse, originalData: Partial<IPrice>[]) => {
    const errors: string[] = [];
    if (Array.isArray(res.data)) {
      (res.data as IBatchRejection[]).forEach((result, index) => {
        if (result.status === 'rejected') {
          const failedItem = originalData[index]?.PTB_ITEM || `Index: ${index}`;
          errors.push(`${failedItem}: ${result.reason?.errors?.errorMessage || 'ล้มเหลว'}`);
        }
      });
    } else if (res.status !== 'OK' && res.status !== 'fulfilled') {
      errors.push(res.message || 'เกิดข้อผิดพลาดจากเซิร์ฟเวอร์');
    }

    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: <ul style={{ paddingLeft: '15px', maxHeight: '250px', overflowY: 'auto' }}>{errors.map((err, i) => <li key={i}>{err}</li>)}</ul>,
      });
      return true;
    }
    return false;
  };

  const actionPriceMutation = useMutation({
    mutationFn: (payload: Partial<IPrice>[]) => updatePrice(payload),
    onSuccess: (res, variables) => {
      if (!notifyBatchErrors('แก้ไขราคาไม่สำเร็จ', res, variables)) {
        notification.success({ message: 'ดำเนินการสำเร็จ' });
      }
      refetch();
    },
  });

  useEffect(() => {
    // Only use update list as Insert tab is removed
    const list = data?.update;
    setSelected(list?.map(c => ({ ...c, _key: uniqueId('prc-') })) || []);
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [data]);

  // Combined Search Logic
  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lowerSearch = searchText.toLowerCase();
    return selected.filter(item =>
      item.PTB_NBR?.toLowerCase().includes(lowerSearch) ||
      item.PTB_ITEM?.toLowerCase().includes(lowerSearch) ||
      item.PT_DESC1?.toLowerCase().includes(lowerSearch)
    );
  }, [selected, searchText]);

  const columns: TableColumnsType<IPrice> = useMemo(() => [
    {
      title: 'เลขที่เอกสาร',
      dataIndex: 'PTB_NBR',
      width: 160,
      sorter: (a, b) => (a.PTB_NBR || '').localeCompare(b.PTB_NBR || ''),
      filters: Array.from(new Set(selected.map(i => i.PTB_NBR).filter(Boolean)))
        .sort().map(v => ({ text: v as string, value: v as string })),
      onFilter: (value, record) => record.PTB_NBR === value,
      filterSearch: true,
    },
    {
      title: 'รหัสสินค้า',
      dataIndex: 'PTB_ITEM',
      width: 160,
      sorter: (a, b) => (a.PTB_ITEM || '').localeCompare(b.PTB_ITEM || ''),
      filters: Array.from(new Set(selected.map(i => i.PTB_ITEM).filter(Boolean)))
        .sort().map(v => ({ text: v as string, value: v as string })),
      onFilter: (value, record) => record.PTB_ITEM === value,
      filterSearch: true,
    },
    {
      title: 'รายละเอียด',
      dataIndex: 'PT_DESC1',
      ellipsis: true,
      sorter: (a, b) => (a.PT_DESC1 || '').localeCompare(b.PT_DESC1 || ''),
      filters: Array.from(new Set(selected.map(i => i.PT_DESC1).filter(Boolean)))
        .sort().map(v => ({ text: v as string, value: v as string })),
      onFilter: (value, record) => record.PT_DESC1?.includes(value as string) ?? false,
      filterSearch: true,
    },
    {
      title: 'จังหวัด',
      dataIndex: 'PTB_PROVICE',
      width: 140,
      sorter: (a, b) => (a.PTB_PROVICE || '').localeCompare(b.PTB_PROVICE || ''),
      filters: Array.from(new Set(selected.map(i => i.PTB_PROVICE).filter(Boolean)))
        .sort().map(v => ({ text: v as string, value: v as string })),
      onFilter: (value, record) => record.PTB_PROVICE === value,
      filterSearch: true,
    },
    {
      title: 'ราคา',
      dataIndex: 'PTB_PRICE',
      width: 100,
      align: 'right',
      sorter: (a, b) => (a.PTB_PRICE || 0) - (b.PTB_PRICE || 0),
      render: (val) => <b>{Number(val).toLocaleString()}</b>,
    },
    {
      title: 'วันที่เริ่ม',
      dataIndex: 'PTB_EFFDATE',
      width: 110,
      sorter: (a, b) => dayjs(a.PTB_EFFDATE).unix() - dayjs(b.PTB_EFFDATE).unix(),
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'สถานะ',
      dataIndex: 'PTB_SIGN_STATUS',
      width: 120,
      sorter: (a, b) => (a.PTB_SIGN_STATUS || '').localeCompare(b.PTB_SIGN_STATUS || ''),
      filters: Array.from(new Set(selected.map(i => i.PTB_SIGN_STATUS).filter(Boolean)))
        .sort().map(v => ({ text: v as string, value: v as string })),
      onFilter: (value, record) => record.PTB_SIGN_STATUS === value,
      render: (status) => (
        <Badge
          status={status === 'UPDATED' ? 'success' : 'default'}
          text={status || 'PENDING'}
        />
      ),
    },
  ], [selected]);

  return (
    <div style={{ paddingBottom: '20px' }}>
      <Flex justify="space-between" align="center" className="mb-3">
        {/* Tab section removed as per request */}
        <div style={{ fontSize: '16px', fontWeight: 'bold' }}>รายการแก้ไขราคา</div>

        <Flex gap={12}>
          <Input.Search
            placeholder="ค้นหา (เลขที่เอกสาร / รหัสสินค้า / รายละเอียด)"
            allowClear
            onSearch={setSearchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 400 }}
          />

          <button
            type="button"
            className="btn btn-success"
            disabled={!selectedRowKeys.length || actionPriceMutation.isPending}
            onClick={() => actionPriceMutation.mutate(selectedRows)}
          >
            {actionPriceMutation.isPending ? 'กำลังส่ง...' : 'แก้ไขข้อมูลที่ OTOD >'}
          </button>
        </Flex>
      </Flex>

      <Table<IPrice>
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          }
        }}
        loading={isFetching}
        columns={columns}
        dataSource={filteredData}
        rowKey="_key"
        size="small"
        bordered
        pagination={false} // Browser scroll enabled
        scroll={{ x: 1200 }}
        locale={{ emptyText: 'ไม่พบรายการราคา' }}
      />
    </div>
  );
};

export default PricePage;